<script setup lang="ts">

const { locale, t } = useI18n();
const localePath = useLocalePath();

// Phones often miss the default reveal threshold during fast finger-scroll —
// sections flick past while still at opacity:0. Trigger ~280px before the
// section enters the viewport on small screens; keep default on tablet/desktop.
useReveal(
  import.meta.client && window.matchMedia('(max-width: 768px)').matches
    ? { threshold: 0, rootMargin: '0px 0px 280px 0px' }
    : undefined,
);

const SITE_ORIGIN = "https://georgiadream.ge";

const { data: page } = await useAsyncData(
  `page-location-${locale.value}`,
  async () => {
    const collection = ("pages_" + locale.value) as "pages_en";
    return await queryCollection(collection)
      .where("slug", "=", "location")
      .first();
  },
  { watch: [locale] }
);

useLocalisedSeo(page);

const activeDistanceIndex = ref<number | null>(null);
const activeAttractionIndex = ref<number | null>(null);

// Zod defaults `mode` to "taxi" at parse time, so every item has a mode at
// runtime; the generated content type keeps it optional, hence the cast to
// the shape DistancesTable's `block` prop expects.
const distancesBlock = computed(
  () =>
    page.value?.distances as
      | {
          title?: string;
          intro?: string;
          summary?: string;
          items: {
            label: string;
            km: number;
            minutes: number;
            transport: string;
            mode: "walk" | "taxi" | "bus" | "drive";
            note?: string;
            recommended?: boolean;
            lat?: number;
            lng?: number;
          }[];
        }
      | undefined
);
const mapSection = ref<HTMLElement>();

const mapPoints = computed(() =>
  (page.value?.distances?.items || [])
    .map((item) =>
      item.lat != null && item.lng != null
        ? {
            lat: item.lat,
            lng: item.lng,
            label: item.label,
            mode: item.mode,
          }
        : null
    )
    .filter((p): p is NonNullable<typeof p> => p !== null)
);

function focusOnDistance(i: number) {
  activeDistanceIndex.value = i;
  mapSection.value?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function galleryToDistanceIndex(galleryIndex: number): number {
  const gallery = page.value?.attractions_gallery?.items;
  const distances = page.value?.distances?.items;
  if (!gallery || !distances) return -1;
  const attraction = gallery[galleryIndex];
  if (attraction?.lat == null || attraction?.lng == null) return -1;
  return distances.findIndex(
    (d) =>
      d.lat != null &&
      d.lng != null &&
      Math.abs(d.lat - attraction.lat!) < 0.0001 &&
      Math.abs(d.lng - attraction.lng!) < 0.0001
  );
}

function focusFromGallery(galleryIndex: number) {
  const distIdx = galleryToDistanceIndex(galleryIndex);
  if (distIdx === -1) return;
  activeDistanceIndex.value = distIdx;
  mapSection.value?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function onGalleryHover(i: number | null) {
  activeAttractionIndex.value = i;
  if (i == null) {
    activeDistanceIndex.value = null;
    return;
  }
  const distIdx = galleryToDistanceIndex(i);
  if (distIdx !== -1) activeDistanceIndex.value = distIdx;
}

const pageUrl = computed(() => SITE_ORIGIN + localePath("/location"));
const PLACE_ID = `${SITE_ORIGIN}/#dreamland-oasis`;
const ADJARA_ID = `${SITE_ORIGIN}/#adjara`;

const dateModifiedIso = new Date().toISOString().split("T")[0];

useBreadcrumbsLd(() => [
  { name: t("nav.home"), path: "/" },
  { name: t("nav.location"), path: "/location" },
]);

useHead(() => {
  const p = page.value;
  if (!p) return {};

  const graph: Record<string, unknown>[] = [];
  const webpageId = `${pageUrl.value}#webpage`;
  const breadcrumbId = `${pageUrl.value}#breadcrumb`;

  graph.push({
    "@type": "WebPage",
    "@id": webpageId,
    url: pageUrl.value,
    name: p.title,
    description: p.description || p.meta_description,
    inLanguage: locale.value,
    isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    breadcrumb: { "@id": breadcrumbId },
    primaryImageOfPage: p.og_image
      ? { "@type": "ImageObject", url: SITE_ORIGIN + p.og_image }
      : undefined,
    about: { "@id": PLACE_ID },
    dateModified: dateModifiedIso,
  });

  graph.push({
    "@type": "Place",
    "@id": ADJARA_ID,
    name: "Adjara",
    description:
      "Autonomous Republic of Adjara on Georgia's Black Sea coast — capital Batumi.",
    address: {
      "@type": "PostalAddress",
      addressRegion: "Adjara",
      addressCountry: "GE",
    },
  });

  graph.push({
    "@type": "Place",
    "@id": PLACE_ID,
    name: "Dreamland Oasis Chakvi",
    description:
      "Apartment resort on the Black Sea coast in Chakvi, Adjara. Private beach, four pools, aquapark.",
    url: `${SITE_ORIGIN}/`,
    geo: {
      "@type": "GeoCoordinates",
      latitude: p.coords?.lat,
      longitude: p.coords?.lng,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Batumi st. 16",
      addressLocality: "Chakvi",
      addressRegion: "Adjara",
      postalCode: "6200",
      addressCountry: "GE",
    },
    containedInPlace: { "@id": ADJARA_ID },
  });

  if (p.attractions_gallery?.items?.length) {
    p.attractions_gallery.items.forEach((it) => {
      if (it.lat == null || it.lng == null) return;
      const node: Record<string, unknown> = {
        "@type": "TouristAttraction",
        "@id": `${pageUrl.value}#${it.slug}`,
        name: it.label,
        description: it.short_description,
        image: SITE_ORIGIN + it.image,
        url: `${pageUrl.value}#${it.slug}`,
        inLanguage: locale.value,
        geo: {
          "@type": "GeoCoordinates",
          latitude: it.lat,
          longitude: it.lng,
        },
        containedInPlace: { "@id": ADJARA_ID },
      };
      if (it.alternate_names && it.alternate_names.length > 0) {
        node.alternateName = it.alternate_names;
      }
      if (it.paid === false) {
        node.isAccessibleForFree = true;
      } else if (it.paid === true) {
        node.isAccessibleForFree = false;
      }
      graph.push(node);
    });
  }

  // FAQPage from the same faq_block the page renders below. Google no longer
  // shows FAQ rich results for commercial sites (gov/health only since Aug
  // 2023), but AI engines (Google AIO, ChatGPT, Perplexity) extract FAQPage
  // markup for citation — and these are real "questions guests ask" passages,
  // exactly what GEO rewards. Source of truth stays the content md.
  if (p.faq_block?.items?.length) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${pageUrl.value}#faq`,
      inLanguage: locale.value,
      isPartOf: { "@id": webpageId },
      mainEntity: p.faq_block.items.map((it) => ({
        "@type": "Question",
        name: it.question,
        acceptedAnswer: { "@type": "Answer", text: it.answer },
      })),
    });
  }

  return {
    link: p.hero_image
      ? [
          {
            rel: "preload",
            as: "image",
            href: p.hero_image,
            fetchpriority: "high",
          },
        ]
      : [],
    script: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": graph,
        }),
      },
    ],
  };
});
</script>

<template>
  <div v-if="page">
    <section
      class="relative flex min-h-[40vh] w-full items-center overflow-hidden bg-cover bg-center py-10 sm:py-14 md:min-h-[52vh] md:py-20"
      :style="
        page.hero_image ? `background-image: url('${page.hero_image}')` : ''
      "
    >
      <div
        class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/70 to-slate-900/90"
      />
      <div class="container relative z-10 text-center">
        <h1
          class="mt-6 font-display text-3xl italic leading-tight text-balance [overflow-wrap:anywhere] text-white drop-shadow sm:text-4xl md:mt-10 md:text-5xl lg:text-6xl"
        >
          {{ page.title }}
        </h1>
        <p
          v-if="page.intro"
          class="mx-auto mt-4 max-w-3xl text-base text-white/90 sm:text-lg md:mt-6 md:text-xl"
        >
          {{ page.intro }}
        </p>
      </div>
    </section>

    <DistancesTable
      v-if="distancesBlock"
      :block="distancesBlock"
      :active-index="activeDistanceIndex"
      @row:hover="activeDistanceIndex = $event"
      @row:focus="focusOnDistance"
    />

    <section
      ref="mapSection"
      class="relative overflow-hidden bg-white dark:bg-slate-900"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
      />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
      />
      <div
        data-reveal
        class="relative mx-auto w-full max-w-7xl px-4 pb-8 pt-2 md:pb-12 md:pt-4"
      >
        <div
          class="rounded-2xl overflow-hidden ring-1 ring-slate-900/5 dark:ring-white/10 shadow-lg shadow-slate-900/5"
        >
          <LocationMap
            v-if="page.coords"
            :center="page.coords"
            :territory="page.territory"
            :points="mapPoints"
            :active-index="activeDistanceIndex"
            :title="page.title"
            poster="/images/complex/complex1.jpg"
            height="clamp(300px, 56vw, 560px)"
            @point:hover="activeDistanceIndex = $event"
          />
        </div>
        <NativeMapsLinks
          v-if="page.coords"
          :lat="page.coords.lat"
          :lng="page.coords.lng"
          :name="page.title || 'Dreamland Oasis Chakvi'"
        />
      </div>
    </section>

    <LocationDayRhythm v-if="page.rhythm_block" :block="page.rhythm_block" />

    <LocationAttractionsGallery
      v-if="page.attractions_gallery"
      :block="page.attractions_gallery"
      :active-index="activeAttractionIndex"
      @card:hover="onGalleryHover"
      @card:focus="focusFromGallery"
    />

    <LocationTransportBlock
      v-if="page.transport_block"
      :block="page.transport_block"
    />

    <LocationSeasonalGuide
      v-if="page.seasonal_guide"
      :block="page.seasonal_guide"
    />

    <LocationFaqAccordion v-if="page.faq_block" :block="page.faq_block" />

    <AboutFurtherBlock v-if="page.further" :block="page.further" />

    <section
      class="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white py-8 md:py-12 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
      />
      <div
        data-reveal
        class="relative container mx-auto max-w-3xl px-4 text-center"
      >
        <h2
          class="font-display text-3xl italic leading-tight text-slate-900 md:text-4xl dark:text-white"
        >
          {{ $t("location.cta.final.heading") }}
        </h2>
        <p
          class="mt-4 font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ $t("location.cta.final.subtext") }}
        </p>
        <div class="mt-8">
          <NuxtLink
            :to="localePath('/search')"
            class="inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3 font-body text-sm font-semibold uppercase tracking-[0.18em] text-slate-900 shadow-sm transition-colors hover:bg-amber-300"
          >
            {{ $t("amenities.cta.view_apartments") }}
            <svg
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
